import React from 'react';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import { getSession, getCurrentAccountUUID } from 'Vulture/Session';
import { broncoURL } from 'Vulture/ENV.js';

import style from './RoleManagement.module.css';

// A role carrying the scope:global permission is global (account-less) and applies across
// every account; a role with any other scope binds to the account it's created in.
const SCOPE_GLOBAL = 'scope:global';

class RoleManagement extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			roles:             [],
			permissions:       [],
			loading:           true,
			editingRole:       null,
			showCreateModal:   false,
			newRoleName:       '',
			newRoleDesc:       '',
			selectedPermUUIDs: {},
			error:             null,
			saving:            false,
			myRoleUUIDs:       {},
		};
	}

	// Roles the logged-in user holds (any account). Permission edits to these are
	// blocked server-side (self-escalation/self-lockout guard in HandleUpdateRole),
	// so the modal shows them read-only instead of letting the save 403.
	fetchMyGrants() {
		const session = getSession();
		if (!session || !session.user) {
			return;
		}
		this.apiRequest(`/api/user/${session.user}/grants`, { method: 'GET' })
			.then((data) => {
				const myRoleUUIDs = {};
				(data.grants || []).forEach((g) => {
					myRoleUUIDs[g.permission_group_uuid] = true;
				});
				this.setState({ myRoleUUIDs });
			})
			.catch((err) => {
				// Non-fatal: the server guard still blocks a self-edit; we just lose
				// the nicer read-only presentation.
				console.error('Error fetching own grants:', err);
			});
	}

	isOwnRole(role) {
		return !!(role && this.state.myRoleUUIDs[role.uuid]);
	}

	// The scope perms in the catalog (category "scope").
	scopePerms() {
		return this.state.permissions.filter((p) => p.category === 'scope');
	}

	// toggleScope enforces the scope rules: `global` and `account` are exclusive (each
	// clears everything else — global = see all, account = the plain account floor); the
	// sub-scopes (property/slideshow/folder) are combinable and each implies the account
	// floor, so selecting one clears global/account but leaves the other sub-scopes. This
	// is what lets a role be, e.g., slideshow + folder scoped.
	toggleScope(perm) {
		this.setState((prev) => {
			const updated = { ...prev.selectedPermUUIDs };
			const scopePerms = this.scopePerms();
			const name = perm.display_name;
			const isExclusive = (name === 'scope:global' || name === 'scope:account');
			const wasChecked = !!updated[perm.uuid];

			if (isExclusive) {
				// Clear every scope perm, then set this one (unless we're toggling it off).
				scopePerms.forEach((p) => delete updated[p.uuid]);
				if (!wasChecked) {
					updated[perm.uuid] = true;
				}
			} else {
				// Sub-scope: clear global/account (they're mutually exclusive with limits),
				// then toggle just this dimension.
				scopePerms.forEach((p) => {
					if (p.display_name === 'scope:global' || p.display_name === 'scope:account') {
						delete updated[p.uuid];
					}
				});
				if (wasChecked) {
					delete updated[perm.uuid];
				} else {
					updated[perm.uuid] = true;
				}
			}
			return { selectedPermUUIDs: updated };
		});
	}

	componentDidMount() {
		this.fetchRoles();
		this.fetchPermissions();
		this.fetchMyGrants();
	}

	// Re-fetch when the selected account changes underneath us (header account switch
	// keeps this component mounted). Roles + permissions are account-scoped, so stale
	// data must be replaced; close any open editor from the previous account.
	componentDidUpdate(prevProps) {
		if (prevProps.accountUUID !== this.props.accountUUID) {
			this.setState({
				editingRole:       null,
				showCreateModal:   false,
				selectedPermUUIDs: {},
				error:             null,
			});
			this.fetchRoles();
			this.fetchPermissions();
			this.fetchMyGrants(); // also retries a failed mount-time fetch
		}
	}

	apiRequest(path, opts = {}) {
		const session = getSession();
		const defaults = {
			headers: {
				'Content-Type':   'application/json',
				'X-Auth-Session': session.session,
			},
		};
		return fetch(`${broncoURL}${path}`, { ...defaults, ...opts })
			.then((res) => {
				if (!res.ok) {
					return res.json().then((d) => {
						throw new Error(d.error || res.statusText);
					});
				}
				return res.json();
			});
	}

	// afterSave: the write already committed — a refetch failure here must not read as
	// a failed save, so say so explicitly (e.g. the save changed the caller's own
	// visibility and GET /api/roles now 403s).
	fetchRoles(afterSave = false) {
		this.apiRequest('/api/roles', { method: 'GET' })
			.then((data) => {
				this.setState({ roles: data.roles || [], loading: false });
			})
			.catch((err) => {
				console.error('Error fetching roles:', err);
				const message = afterSave
					? `Saved, but the role list could not be refreshed (${err.message}). Reload the page to see current roles.`
					: err.message;
				this.setState({ error: message, loading: false });
			});
	}

	fetchPermissions() {
		this.apiRequest('/api/permissions', { method: 'GET' })
			.then((data) => {
				this.setState({ permissions: data.permissions || [] });
			})
			.catch((err) => {
				console.error('Error fetching permissions:', err);
				this.setState({ error: `Could not load permissions: ${err.message}` });
			});
	}

	startEdit(role) {
		const selectedPermUUIDs = {};
		(role.permissions || []).forEach((p) => {
			selectedPermUUIDs[p.uuid] = true;
		});

		this.setState({
			editingRole:      role,
			showCreateModal:  false,
			newRoleName:      role.display_name,
			newRoleDesc:      role.description || '',
			selectedPermUUIDs,
			error:            null,
		});
	}

	startCreate() {
		this.setState({
			editingRole:      null,
			showCreateModal:  true,
			newRoleName:      '',
			newRoleDesc:      '',
			selectedPermUUIDs: {},
			error:             null,
		});
	}

	cancelEdit() {
		this.setState({
			editingRole:       null,
			showCreateModal:   false,
			selectedPermUUIDs: {},
			error:             null,
		});
	}

	togglePermission(permUUID) {
		this.setState((prev) => {
			const updated = { ...prev.selectedPermUUIDs };
			if (updated[permUUID]) {
				delete updated[permUUID];
			} else {
				updated[permUUID] = true;
			}
			return { selectedPermUUIDs: updated };
		});
	}

	saveRole() {
		const {
			editingRole, newRoleName, newRoleDesc, selectedPermUUIDs, saving,
		} = this.state;

		if (saving) {
			return;
		}
		if (!newRoleName.trim()) {
			this.setState({ error: 'Role name is required' });
			return;
		}

		const permUUIDs = Object.keys(selectedPermUUIDs).filter((k) => {
			return selectedPermUUIDs[k];
		});

		this.setState({ saving: true, error: null });

		if (editingRole) {
			// The grants fetch is async, so the caller's own role can flip read-only
			// AFTER the modal opened with live checkboxes. Never silently drop edits:
			// if they changed the perms of a role that turned out to be their own,
			// stop with the explanation instead of saving without them.
			if (!editingRole.is_system_default && this.isOwnRole(editingRole)) {
				const current = {};
				(editingRole.permissions || []).forEach((p) => {
					current[p.uuid] = true;
				});
				const permsChanged = permUUIDs.length !== Object.keys(current).length
					|| permUUIDs.some((u) => !current[u]);
				if (permsChanged) {
					this.setState({
						error:  'This role is assigned to you — another admin must change its permissions. Your permission changes were not saved.',
						saving: false,
					});
					return;
				}
			}

			const body = {
				display_name: newRoleName.trim(),
				description:  newRoleDesc.trim(),
			};
			// Permission edits are omitted (not just ignored) for system defaults and
			// the caller's own roles — the server 403s both.
			if (!editingRole.is_system_default && !this.isOwnRole(editingRole)) {
				body.permission_uuids = permUUIDs;
			}

			this.apiRequest(`/api/roles/${editingRole.uuid}`, {
				method: 'PUT',
				body:   JSON.stringify(body),
			})
				.then(() => {
					this.setState({ saving: false });
					this.cancelEdit();
					this.fetchRoles(true);
				})
				.catch((err) => {
					this.setState({ error: err.message, saving: false });
				});
		} else {
			const body = {
				display_name:     newRoleName.trim(),
				description:      newRoleDesc.trim(),
				permission_uuids: permUUIDs,
			};
			// Account binding follows the role's SCOPE, not the caller: a global-scoped
			// role (scope:global) is account-less and applies everywhere; every other
			// scope binds to the account the admin is working in. So copying internal_admin
			// stays global, but copying property_manager binds to the current account.
			const scopeGlobalPerm = this.state.permissions.find((p) => {
				return p.category === 'scope' && p.display_name === SCOPE_GLOBAL;
			});
			const isGlobalRole = !!(scopeGlobalPerm && selectedPermUUIDs[scopeGlobalPerm.uuid]);
			if (!isGlobalRole) {
				const acct = getCurrentAccountUUID();
				if (!acct) {
					this.setState({ error: 'Select an account before creating a non-global role.', saving: false });
					return;
				}
				body.account_uuid = acct;
			}

			this.apiRequest('/api/roles', {
				method: 'POST',
				body:   JSON.stringify(body),
			})
				.then(() => {
					this.setState({ saving: false });
					this.cancelEdit();
					this.fetchRoles(true);
				})
				.catch((err) => {
					this.setState({ error: err.message, saving: false });
				});
		}
	}

	// copyRole opens the CREATE modal prefilled from an existing role (name "Copy of X"
	// + its permissions) so the admin can rename/tweak BEFORE anything is written. The
	// actual role is created only on Save (saveRole → POST /api/roles), which binds it by
	// the copied role's scope: a global-scoped copy stays global, any other scope binds to
	// the current account. Nothing is written on the button click itself.
	copyRole(role) {
		const selectedPermUUIDs = {};
		(role.permissions || []).forEach((p) => {
			selectedPermUUIDs[p.uuid] = true;
		});
		this.setState({
			editingRole:       null, // create mode
			showCreateModal:   true,
			newRoleName:       `Copy of ${role.display_name}`,
			newRoleDesc:       role.description || '',
			selectedPermUUIDs,
			error:             null,
		});
	}

	deleteRole(roleUUID) {
		if (this.state.saving) {
			return;
		}
		if (!window.confirm('Are you sure you want to delete this role?')) {
			return;
		}

		this.setState({ saving: true });
		this.apiRequest(`/api/roles/${roleUUID}`, { method: 'DELETE' })
			.then(() => {
				this.setState({ saving: false });
				this.fetchRoles();
			})
			.catch((err) => {
				this.setState({ saving: false });
				alert(err.message);
			});
	}

	groupPermissionsByCategory() {
		const { permissions } = this.state;
		const grouped = {};
		permissions.forEach((p) => {
			if (!grouped[p.category]) {
				grouped[p.category] = [];
			}
			grouped[p.category].push(p);
		});
		return grouped;
	}

	renderPermissionMatrix() {
		const { selectedPermUUIDs, editingRole } = this.state;
		const grouped = this.groupPermissionsByCategory();
		const isSystemDefault = editingRole?.is_system_default;
		const isReadOnly = isSystemDefault || this.isOwnRole(editingRole);
		const categories = Object.keys(grouped).sort();

		return (
			<div className={style.permissionMatrix}>
				{categories.map((category) => {
					// Scope perms are multi-select: global/account are exclusive, but the
					// sub-scopes (property/slideshow/folder) can be combined, so the whole
					// "scope" category renders as checkboxes with toggleScope enforcing the
					// exclusivity rules.
					const isScope = category === 'scope';
					return (
						<div key={category} className={style.permCategory}>
							<div className={style.categoryHeader}>{category}</div>
							<div className={style.permList}>
								{grouped[category].map((perm) => {
									const checked = !!selectedPermUUIDs[perm.uuid];
									return (
										<label key={perm.uuid} className={style.permItem}>
											<input
												type="checkbox"
												checked={checked}
												disabled={isReadOnly}
												onChange={() => {
													if (isScope) {
														this.toggleScope(perm);
													} else {
														this.togglePermission(perm.uuid);
													}
												}}
											/>
											<span className={style.permName}>
												{perm.display_name.replace(/_/g, ' ')}
											</span>
										</label>
									);
								})}
							</div>
						</div>
					);
				})}
			</div>
		);
	}

	renderEditModal() {
		const {
			editingRole, showCreateModal, newRoleName, newRoleDesc, error,
		} = this.state;

		if (!editingRole && !showCreateModal) return null;

		const title = editingRole ? `Edit Role: ${editingRole.display_name}` : 'Create New Role';
		const isSystemDefault = editingRole?.is_system_default;

		return (
			<div className={style.modalOverlay}>
				<div className={style.modal}>
					<div className={style.modalHeader}>
						<h3>{title}</h3>
						<button
							className={style.closeButton}
							onClick={() => {
								this.cancelEdit();
							}}
						>
							<CustomIcon icon="times" />
						</button>
					</div>

					{error && (
						<div className={style.errorBanner}>{error}</div>
					)}

					<div className={style.modalBody}>
						<div className={style.formField}>
							<label>Role Name</label>
							<input
								type="text"
								value={newRoleName}
								onChange={(e) => {
									this.setState({ newRoleName: e.target.value });
								}}
								placeholder="e.g. Content Editor"
							/>
						</div>

						<div className={style.formField}>
							<label>Description</label>
							<input
								type="text"
								value={newRoleDesc}
								onChange={(e) => {
									this.setState({ newRoleDesc: e.target.value });
								}}
								placeholder="Short description of this role"
							/>
						</div>

						{/* Scope is now a permission: pick a scope:* perm in the
						    permission matrix below (the "scope" category). */}

						<div className={style.formField}>
							<label>
								Permissions
								{isSystemDefault && (
									<span className={style.readOnlyBadge}>Read-only (system role)</span>
								)}
								{!isSystemDefault && this.isOwnRole(editingRole) && (
									<span className={style.readOnlyBadge}>
										Read-only (this role is assigned to you — another admin must change its permissions)
									</span>
								)}
							</label>
							{this.renderPermissionMatrix()}
						</div>
					</div>

					<div className={style.modalFooter}>
						<button
							className="button"
							disabled={this.state.saving}
							onClick={() => {
								this.cancelEdit();
							}}
						>
							Cancel
						</button>
						<button
							className="button"
							disabled={this.state.saving}
							onClick={() => {
								this.saveRole();
							}}
						>
							<CustomIcon icon="save" />
							{this.state.saving ? 'Saving...' : (editingRole ? 'Save Changes' : 'Create Role')}
						</button>
					</div>
				</div>
			</div>
		);
	}

	render() {
		const {
			roles, loading, error, editingRole, showCreateModal,
		} = this.state;

		if (loading) {
			return (
				<div className={style.container}>
					<div className={style.loading}>
						<CustomIcon icon="spinner" spin={true} /> Loading roles...
					</div>
				</div>
			);
		}

		// The roles page is account-scoped: show account-less roles (system defaults +
		// global customs) plus custom roles bound to the account selected in the header.
		// Custom roles from OTHER accounts are hidden here. (/api/roles returns the full
		// visible set because the assignment dropdowns filter per-account-card separately.)
		const currentAccount = getCurrentAccountUUID();
		const visibleRoles = roles.filter((role) => {
			return !role.account_uuid || role.account_uuid === currentAccount;
		});

		return (
			<div className={style.container}>
				<div className={style.header}>
					<h2>Role Management</h2>
					<button
						className="button"
						onClick={() => {
							this.startCreate();
						}}
					>
						<CustomIcon icon="plus" /> New Role
					</button>
				</div>

				{/* Errors surface in the modal while it's open; a refetch failure after the
				    modal closes (e.g. a save revoked the caller's own list access) would
				    otherwise be invisible. */}
				{error && !editingRole && !showCreateModal && (
					<div className={style.errorBanner}>{error}</div>
				)}

				<div className={style.roleList}>
					<div className={style.roleHeader}>
						<span className={style.colName}>Role Name</span>
						<span className={style.colPerms}>Permissions</span>
						<span className={style.colUsers}>Users</span>
						<span className={style.colActions}>Actions</span>
					</div>

					{visibleRoles.map((role) => {
						return (
							<div key={role.uuid} className={style.roleRow}>
								<span className={style.colName}>
									{role.display_name.replace(/_/g, ' ')}
									{role.is_system_default && (
										<span className={style.systemBadge}>system</span>
									)}
									{role.default_scope_type && (
										<span className={style.scopeTag}>{role.default_scope_type}</span>
									)}
								</span>
								<span className={style.colPerms}>{(role.permissions || []).length}</span>
								<span className={style.colUsers}>{role.user_count}</span>
								<span className={style.colActions}>
									<button
										className={style.actionBtn}
										disabled={this.state.saving}
										onClick={() => {
											this.copyRole(role);
										}}
										title="Copy to a new custom role"
									>
										<CustomIcon icon="copy" />
									</button>
									<button
										className={style.actionBtn}
										onClick={() => {
											this.startEdit(role);
										}}
										title="Edit"
									>
										<CustomIcon icon="pen" />
									</button>
									{!role.is_system_default && (
										<button
											className={style.actionBtn}
											disabled={this.state.saving}
											onClick={() => {
												this.deleteRole(role.uuid);
											}}
											title="Delete"
										>
											<CustomIcon icon="trash" />
										</button>
									)}
								</span>
							</div>
						);
					})}
				</div>

				{this.renderEditModal()}
			</div>
		);
	}
}

export default RoleManagement;
